import type { Character } from '../domain/character.ts';
import type { SessionState } from '../domain/session.ts';
import type { RuntimeState } from '../domain/runtime.ts';
import type { CharacterRepository } from '../persistence/characterRepository.ts';
import type { SessionRepository } from '../persistence/sessionRepository.ts';
import type { RuntimeRepository } from '../persistence/runtimeRepository.ts';
import type { CheckpointCommitter } from '../persistence/checkpointCommitter.ts';

/** The three records a play sheet holds, all present. */
export interface SheetRecords {
  character: Character;
  session: SessionState;
  runtime: RuntimeState;
}

/**
 * What a mutation decided. Only the records it CHANGED are set; anything left
 * out is untouched and is not written.
 */
export interface MutationRecords {
  character?: Character | undefined;
  session?: SessionState | undefined;
  runtime?: RuntimeState | undefined;
}

export interface MutationPersistenceDeps {
  characters: CharacterRepository;
  sessions: SessionRepository;
  runtimes: RuntimeRepository;
  checkpoints: CheckpointCommitter;
}

export interface PersistedMutation {
  /** What was actually SAVED, stamped, ready to go into state. */
  records: SheetRecords;
  /** How many records moved. */
  changed: number;
  /**
   * Whether the journalled committer was used. A failure on this path may have
   * left a pending journal; a failure on the single-record path cannot have.
   */
  usedCommitter: boolean;
}

/**
 * Which records moved, and therefore which boundary this mutation will use.
 *
 * Exposed SEPARATELY from the write so a caller can know the answer BEFORE it
 * awaits. That matters for error handling: a failure on the committer path may
 * have left a pending journal and must put the sheet into recovery-required
 * mode, and a failure on the single-record path cannot have and must not. A
 * flag set after the await is no use when the await is what threw.
 */
export function mutationRouting(
  current: SheetRecords,
  next: MutationRecords,
): {
  character: Character | undefined;
  session: SessionState | undefined;
  runtime: RuntimeState | undefined;
  changed: number;
  usesCommitter: boolean;
} {
  const character =
    next.character && next.character !== current.character ? next.character : undefined;
  const session = next.session && next.session !== current.session ? next.session : undefined;
  const runtime = next.runtime && next.runtime !== current.runtime ? next.runtime : undefined;
  const changed = [character, session, runtime].filter((r) => r !== undefined).length;
  return { character, session, runtime, changed, usesCommitter: changed > 1 };
}

/**
 * Writes a mutation through the RIGHT boundary and returns what landed.
 *
 * The choice is made by how many records moved, so a change to one record does
 * not pay for a journal it does not need:
 *
 * - **none** - nothing is written.
 * - **one** - its own repository. There is no second record that could fall out
 *   of step, so a journal would buy nothing.
 * - **two or more** - `CheckpointCommitter`. One logical game operation is one
 *   transaction. A Check that marks a Skill, writes a roll and expires Stunned
 *   must land all three or none: keeping the mark while losing the roll that
 *   earned it is not a state the rules have a name for.
 *
 * Records are compared BY REFERENCE against the current ones, so a caller
 * passing something back unchanged does not cause a pointless write - or, worse,
 * an unnecessary journal.
 *
 * Whatever the boundary, the SAVED object comes back. The repositories stamp
 * `updatedAt`, and a caller that keeps its pre-save copy is holding a record the
 * file does not agree with.
 *
 * This is application policy, not a React concern, which is why it lives here:
 * the screen owns the LOCK, this owns the ROUTING, and both can be tested.
 */
export async function persistMutation(
  current: SheetRecords,
  next: MutationRecords,
  deps: MutationPersistenceDeps,
): Promise<PersistedMutation> {
  const { character, session, runtime, changed, usesCommitter } = mutationRouting(current, next);
  if (changed === 0) return { records: current, changed: 0, usedCommitter: false };

  if (!usesCommitter) {
    return {
      records: {
        character: character ? await deps.characters.save(character) : current.character,
        session: session ? await deps.sessions.save(session) : current.session,
        runtime: runtime ? await deps.runtimes.save(runtime) : current.runtime,
      },
      changed,
      usedCommitter: false,
    };
  }

  // The unchanged records are passed through so the committer has a complete
  // set. `runtime` stays undefined when it did not move, which keeps that a
  // two-record transaction rather than rewriting a record nobody touched.
  const committed = await deps.checkpoints.commit(
    character ?? current.character,
    session ?? current.session,
    runtime,
  );
  return {
    records: {
      character: committed.character,
      session: committed.session,
      runtime: committed.runtime ?? current.runtime,
    },
    changed,
    usedCommitter: true,
  };
}
